/** An activity's documents & links — the activity record's immediate panel (plan §3, O12,
 *  §9.6): stored files and links the app shows on the activity, and on every tour stop whose
 *  documents are "Use activity documents" (`inherited_by`). Every write is saved at once and
 *  audited server-side (`activity.document.created / updated / deleted / reordered`, with the
 *  values) — never part of the record's draft.
 *
 *  - `GET  /activities/{pk}/documents/`             — the rows in order, and `inherited_by`
 *    (`console_activities_view`);
 *  - `POST /activities/{pk}/documents/`             — a file (multipart `_json` + `file`, at
 *    most 32 MB, never HTML / SVG / XML / scripts) or a link (JSON `url`), at the end;
 *  - `PATCH  /activities/{pk}/documents/{doc_id}/`  — rename (the keys sent change);
 *  - `DELETE /activities/{pk}/documents/{doc_id}/`;
 *  - `POST /activities/{pk}/documents/reorder/`     — `{ids}`: exactly the rows, in order.
 *
 *  Writes need `console_activities_manage`; an activity without an operator refuses them
 *  (`not_editable`). A refusal is `{detail, code, field?}` (400), or the 413 of a file over
 *  32 MB — read through `catalogWriteErrors`. Each write refreshes the list, and the detail
 *  (its `document_count` is the band's Content cell). */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { activityDetailKey } from './activityRecord';
import { apiDelete, apiGet, apiPatch, apiPost, client } from './client';
import type { components } from './types';

export type ActivityDocument = components['schemas']['ConsoleActivityDocument'];
export type ActivityDocumentList = components['schemas']['ConsoleActivityDocumentList'];
export type ActivityDocumentWrite = components['schemas']['ConsoleActivityDocumentWrite'];
export type ActivityDocumentError = components['schemas']['ConsoleActivityDocumentError'];

/** The documents' upload cap (the server's `MAX_DOCUMENT_BYTES`). */
export const ACTIVITY_DOCUMENT_MAX_BYTES = 32 * 1024 * 1024;

/** Under `['activities']`, so an activity write refreshes it too. */
export const activityDocumentsKey = (id: number) => ['activities', 'documents', id] as const;

const base = (id: number) => `/activities/${id}/documents/`;

export function listActivityDocuments(id: number): Promise<ActivityDocumentList> {
  return apiGet<ActivityDocumentList>(base(id));
}

/** A file, at the end of the list: its name defaults to the file's. */
export async function uploadActivityDocument(
  id: number,
  file: File,
  meta: Omit<ActivityDocumentWrite, 'url'> = {},
): Promise<ActivityDocument> {
  const fd = new FormData();
  fd.append('_json', JSON.stringify(meta));
  fd.append('file', file);
  const r = await client.post<ActivityDocument>(base(id), fd, { headers: { 'Content-Type': undefined } });
  return r.data;
}

/** A link, at the end of the list: its name defaults to the address. */
export function addActivityDocumentLink(
  id: number,
  body: ActivityDocumentWrite & { url: string },
): Promise<ActivityDocument> {
  return apiPost<ActivityDocument>(base(id), body);
}

export function updateActivityDocument(
  id: number,
  docId: number,
  body: ActivityDocumentWrite,
): Promise<ActivityDocument> {
  return apiPatch<ActivityDocument>(`${base(id)}${docId}/`, body);
}

export function removeActivityDocument(id: number, docId: number): Promise<void> {
  return apiDelete<void>(`${base(id)}${docId}/`);
}

export function reorderActivityDocuments(id: number, ids: number[]): Promise<ActivityDocumentList> {
  return apiPost<ActivityDocumentList>(`${base(id)}reorder/`, { ids });
}

// ---- React Query -----------------------------------------------------------

export function useActivityDocuments(
  id: number,
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<ActivityDocumentList> {
  return useQuery({ queryKey: activityDocumentsKey(id), queryFn: () => listActivityDocuments(id), enabled });
}

/** The panel's writes. Each refreshes the list and the detail once it settles; a reorder
 *  shows the new order at once and goes back to the stored one when it is refused. */
export function useActivityDocumentWrites(id: number) {
  const qc = useQueryClient();
  const key = activityDocumentsKey(id);
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: key }),
      qc.invalidateQueries({ queryKey: activityDetailKey(id), exact: true }),
    ]);
  const upload = useMutation({
    mutationFn: ({ file, name }: { file: File; name?: string }) =>
      uploadActivityDocument(id, file, name ? { name } : {}),
    onSuccess: refresh,
  });
  const addLink = useMutation({
    mutationFn: (body: { url: string; name?: string }) => addActivityDocumentLink(id, body),
    onSuccess: refresh,
  });
  const rename = useMutation({
    mutationFn: ({ docId, name }: { docId: number; name: string }) => updateActivityDocument(id, docId, { name }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (docId: number) => removeActivityDocument(id, docId),
    onSuccess: refresh,
  });
  const reorder = useMutation({
    mutationFn: (ids: number[]) => reorderActivityDocuments(id, ids),
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<ActivityDocumentList>(key);
      if (previous) {
        const byId = new Map(previous.results.map((d) => [d.id, d]));
        const results = ids.flatMap((docId) => {
          const d = byId.get(docId);
          return d ? [d] : [];
        });
        qc.setQueryData<ActivityDocumentList>(key, { ...previous, results });
      }
      return { previous };
    },
    onError: (_e, _ids, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },
    onSuccess: (fresh) => qc.setQueryData(key, fresh),
    onSettled: refresh,
  });
  return { upload, addLink, rename, remove, reorder };
}
